import { Grid, Typography, useMediaQuery } from "@mui/material";
import CheckSinglePerm from "components/shared/CheckPerm";
import { handlePrintCsv } from "components/utils/handlers";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDevicesSocket,
  setUpDeviceSocket,
  unSubscribeSocketTopics,
} from "../../../redux/saga/socket";
import { accessActions } from "../../../redux/slice/access.slice";
import { alertsActions } from "../../../redux/slice/alerts.slice";
import { authActions } from "../../../redux/slice/auth.slice";
import { devicesActions } from "../../../redux/slice/devices.slice";
import { uiActions } from "../../../redux/slice/ui.slice";
import { VERIFY_TWO_FA_OTP } from "../../../redux/types/auth.types";
import {
  GET_DEVICES,
  GET_DEVICES_LOCATION,
  GET_DEVICE_TOKEN,
  UPDATE_DEVICE_NAME,
  UPDATE_DEVICE_STATUS,
} from "../../../redux/types/devices.types";
import { FETCH_GROUPS } from "../../../redux/types/group.types";
import { GET_ALL_ALERTS } from "../../../redux/types/multi.types";
import CustomTitle from "../../shared/CustomTitle";
import CustomTableContainer from "../../shared/Tables/CustomTableContainer";
import text from "../../shared/css/text.module.scss";
import { DeviceStatus, TOPICS } from "../../utils/enums";
import theme from "../../utils/theme";
import { USER_PERMISSIONS } from "../Management/AccessManagement/helper";
import SecurityAlerts from "../MultiDashboard/SecurityAlerts";
import ThreatComparison from "../MultiDashboard/ThreatComparison";
import { handleOpenBlockHash } from "../Networks/NetworkKeyFeatures/MapSummary/helper";
import AddDeviceDialog from "./AddDeviceDialogue";
import DeviceMap from "./DeviceMap/DeviceMap";
import DeviceStats from "./DeviceStats";
import TwoFADialog from "./TwoFADialog";
import { getDeviceColumns, getMarker, prepareCentOSCommand, prepareKaliCommand, prepareUbuntuCommand, prepareWindowsCommand } from "./helper";
import { checkSinglePermission } from "components/shared/helper";

const Device = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(authActions.selectors.getCurrentUser);
  const isTwoFaEnabled = currentUser?.isTwoFaEnabled || false;
  const devices = useSelector(devicesActions.selectors.getDevices);
  const devicesLocs = useSelector(devicesActions.selectors.getDeviceLocation);
  const allAlerts = useSelector(alertsActions.selectors.getAllAlerts);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [curlCommand, setCurlCommand] = useState("");
  const [copied, setCopied] = useState(false);
  const [selectedOs, setSelectedOs] = useState("windows");
  const [selectedVersion, setSelectedVersion] = useState("windows11");
  const [selectedDeviceToken, setSelectedDeviceToken] = useState("");
  const [is2FAOpen, setIs2FAOpen] = useState(false);
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"));

  const getDeviceToken = () => {
    const payload = {
      orgId: currentUser?.orgId,
    };
    dispatch({ type: GET_DEVICE_TOKEN, payload });
  };

  useEffect(() => {
    getDevicesSocket({
      orgId: currentUser?.orgId,
      deviceType: "device",
    });
  }, [currentUser?.orgId]);

  useEffect(() => {
    setUpDeviceSocket(dispatch);
    dispatch({ type: GET_ALL_ALERTS });
    return () => {
      unSubscribeSocketTopics([TOPICS.DEVICES]);
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(uiActions.actions.setSelectedNav("Devices"));
    dispatch({ type: GET_DEVICES });
    dispatch({ type: GET_DEVICES_LOCATION });
    dispatch({ type: FETCH_GROUPS });
  }, [dispatch]);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleVerify2FA = async (data) => {
    const paylaod = {
      twoFaOtp: data,
      email: currentUser?.email,
    };
    dispatch({ type: VERIFY_TWO_FA_OTP, paylaod });
    setIs2FAOpen(false);
  };

  const handleClose2FA = () => {
    setIs2FAOpen(false);
  };

  const [isDeviceOpen, setIsDeviceOpen] = useState(false);

  const handleInstallDevice = (token) => {
    setIsDeviceOpen(!isDeviceOpen);
    setSelectedDeviceToken(token);
    if (!isDeviceOpen) {
      switch (selectedOs) {
        case "windows":
          setCurlCommand(prepareWindowsCommand(token));
          break;
        case "ubuntu":
          setCurlCommand(prepareUbuntuCommand(token));
          break;
        case "centos":
          setCurlCommand(prepareCentOSCommand(token));
          break;
        case "kali":
          setCurlCommand(prepareKaliCommand(token));
          break;
        default:
          setCurlCommand("");
      }
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const userPermissions = useSelector(accessActions.selectors.getUserPermissions)


  const handleClick = (event, rowId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleClose = (event, type, row) => {
    event.stopPropagation();
    setAnchorEl(null);
    setMenuRowId(null);
    switch (type) {
      case "install":
        handleInstallDevice(row?.token);
        break;
      case "txn":
        handleOpenBlockHash(row?.assetIdTxn)
        break;
      case "status":
        const payload = {
          deviceId: row?._id,
          deviceStatus: row?.status === DeviceStatus.quarantine ? DeviceStatus.connected : DeviceStatus.quarantine
        }
        dispatch({ type: UPDATE_DEVICE_STATUS, payload });
        break;

      default:
    }
    setAnchorEl(null);
  };

  const deviceCols = getDeviceColumns({
    dispatch,
    isTwoFaEnabled,
    setIs2FAOpen,
    user: currentUser,
    anchorEl,
    handleClose,
    handleOpenMenu: handleClick,
    menuRowId,
    isConsoleAllowed: checkSinglePermission(USER_PERMISSIONS.ACCESS_SINGLE_DEVICE_CONSOLE, userPermissions, currentUser?.role),
    isCompactLayout,
  });

  const handleAddSingleDevice = () => {
    getDeviceToken();

  };

  const markers = useMemo(() => {
    if (devicesLocs?.length) {
      return devicesLocs.map((device) => {
        const loc = device?.loc;
        if (loc) {
          const coordinates = loc.split(",").map(Number)?.reverse();
          const html = getMarker(theme.palette.success.main);
          return { coordinates, html, device: device?.device };
        }
        return null
      })
    }
  }, [devicesLocs])


  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const filteredDevices = useMemo(() => {
    const filteredByType = devices?.filter((dev) => dev.deviceType === "device");
    if (!searchTerm) return filteredByType;

    const regex = new RegExp(searchTerm, "i");
    return filteredByType.filter((dev) =>
      Object.values(dev).some((value) => regex.test(value))
    );
  }, [searchTerm, devices]);

  const handleUpdateDeviceName = (newRow, oldRow) => {
    if (oldRow?.deviceName === newRow?.deviceName) {
      return
    }
    dispatch({ type: UPDATE_DEVICE_NAME, payload: newRow })
  };

  return (
    <>
      <Grid container>
        <Grid container justifyContent={"center"}>
          <Typography className={text.heading1}>
            Device Inventory Management
          </Typography>
        </Grid>
        <br />
        <Grid container spacing={2} marginTop={"3vh"} alignItems="center">
          <Grid item xs={12}>
            <Typography className={text.heading1}>My Devices</Typography>
          </Grid>
        </Grid>
        <Grid
          my={"15px"}
          container
          sx={{ justifyContent: { xl: "space-between", lg: "center" } }}
          spacing={2}
        >
          <Grid item xs={12} md={6}>
            <DeviceStats devices={devices} />
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <DeviceGraph /> */}
            <SecurityAlerts alerts={allAlerts} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ThreatComparison alerts={allAlerts?.chartData} />
          </Grid>
          <Grid item xs={12} md={6}>
            <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_DEVICE_LOCATION}>
              <DeviceMap markers={markers} center={devicesLocs?.[0]?.loc} />
            </CheckSinglePerm>
          </Grid>
        </Grid>
        <CustomTitle title={"Total Devices"} count={filteredDevices?.length} />
        <Grid my={"3vh"} item xs={12}>
          <CustomTableContainer
            deviceCols={deviceCols}
            devices={filteredDevices}
            handleBtn1Click={handleAddSingleDevice}
            handleRowUpdate={handleUpdateDeviceName}
            isDrawerOpen={isDrawerOpen}
            toggleDrawer={toggleDrawer}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            onSearch={handleSearch}
            onPrint={handlePrintCsv}
            isUninstall={true}
          />
        </Grid>
      </Grid>
      <AddDeviceDialog
        curlCommand={curlCommand}
        setCurlCommand={setCurlCommand}
        handleDeviceDialog={handleInstallDevice}
        isDeviceOpen={isDeviceOpen}
        copied={copied}
        setCopied={setCopied}
        selectedOs={selectedOs}
        setSelectedOs={setSelectedOs}
        selectedVersion={selectedVersion}
        setSelectedVersion={setSelectedVersion}
        selectedDeviceToken={selectedDeviceToken}
      />
      <TwoFADialog
        open={is2FAOpen}
        onClose={handleClose2FA}
        onVerify={handleVerify2FA}
      />
    </>
  );
};

export default Device;
